import {Component, Input, OnInit} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {NgxFileDropEntry, FileSystemFileEntry} from 'ngx-file-drop';

@Component({
  selector: 'app-file-upload-patient',
  templateUrl: './file-upload-patient.component.html',
  styleUrls: ['./file-upload-patient.component.scss']
})
export class FileUploadPatientComponent implements OnInit {
  dragIsIn = false;
  fileTypes = "image/*,application/pdf";
  private fileTypeNames = "a PDF or image";
  @Input() files: File[];
  @Input() pdfOnly: boolean;   // Pick at most one of these
  @Input() imageOnly: boolean; // Pick at most one of these
  @Input() multiple = true;
  @Input() showSubcategoryDropdown = false;
  @Input() programName: string = '';
  errorMessage: string;
  selectedSubcategory: string = '';
  customSubcategory: string = '';
  constructor(public modal: NgbActiveModal) {
  }

  ngOnInit(): void {
    if(this.pdfOnly) {
      this.fileTypes = "application/pdf";
      this.fileTypeNames = "a PDF";
    } else if(this.imageOnly) {
      this.fileTypes="image/*";
      this.fileTypeNames = "an image";
    }
  }

  get fileTypesInstructionFragment(): string {
    if (this.pdfOnly) {
      return 'PDF';
    } else if (this.imageOnly) {
      return 'Image';
     } else {
      return 'Image or PDF';
    }
  }

  fileOver(event: MouseEvent) {
    this.dragIsIn = true;
  }

  fileLeave(event: MouseEvent) {
    this.dragIsIn = false;
  }

  async dropped(files: NgxFileDropEntry[]) {
    this.errorMessage = "";
    this.dragIsIn = false;
    if(files.length > 1 && !this.multiple) {
      this.errorMessage = "You can only upload a single file here";
    } else {
      for (const droppedFile of files) {
        if (droppedFile.fileEntry.isFile) { // e.g. not a directory, just a sanity check
          // Need to await the file so we know whether any valid ones were found for the close check after the loop
          const file = await this.extractFile(droppedFile.fileEntry as FileSystemFileEntry);
          if((!this.imageOnly && file.type === 'application/pdf') ||
             (!this.pdfOnly && /^image\//.test(file.type))) {
            this.files.push(file);
          } else this.errorMessage = "Not "+this.fileTypeNames+" file";
        }
      }
    }
    // Remove auto-submit - files are just added, user must click Submit
  }

  selectSubcategory(subcategory: string) {
    this.selectedSubcategory = subcategory;
    if (subcategory !== 'Other') {
      this.customSubcategory = ''; // Clear custom text when selecting predefined options
    }
    this.errorMessage = ''; // Clear any previous error
  }

  onCustomSubcategoryChange() {
    this.errorMessage = ''; // Clear any previous error when typing
  }

  getDisplaySubcategory(): string {
    return this.selectedSubcategory;
  }

  get hasSelectedFile(): boolean {
    return this.files && this.files.length > 0;
  }

  get selectedFileName(): string {
    return this.hasSelectedFile ? this.files[0].name : '';
  }

  get selectedFileSize(): string {
    if (!this.hasSelectedFile) return '';
    const file = this.files[0];
    const sizeInMB = (file.size / (1024 * 1024)).toFixed(2);
    return `${sizeInMB} MB`;
  }

  clearSelectedFile() {
    this.files.length = 0;
    this.errorMessage = '';
  }

  get canSubmit(): boolean {
    const hasFiles = this.files && this.files.length > 0;
    if (this.showSubcategoryDropdown) {
      const hasValidSubcategory = !!this.selectedSubcategory &&
        (this.selectedSubcategory !== 'Other' || (this.selectedSubcategory === 'Other' && !!this.customSubcategory.trim()));
      return hasFiles && hasValidSubcategory;
    }
    return hasFiles;
  }

  submit() {
    if (!this.canSubmit) {
      if (this.showSubcategoryDropdown && !this.selectedSubcategory) {
        this.errorMessage = "Please select a document type";
      } else if (this.selectedSubcategory === 'Other' && !this.customSubcategory.trim()) {
        this.errorMessage = "Please specify the document type";
      } else if (!this.files || this.files.length === 0) {
        this.errorMessage = "Please select a file";
      }
      return;
    }

    // Return the custom subcategory if "Other" was selected, otherwise return the selected subcategory
    const finalSubcategory = this.selectedSubcategory === 'Other' && this.customSubcategory.trim()
      ? this.customSubcategory.trim()
      : this.selectedSubcategory;

    this.modal.close(finalSubcategory);
  }

  extractFile(source:FileSystemFileEntry):Promise<File> {
    return new Promise(resolve => source.file(file => resolve(file)));
  }
}
